import config from './config';

export async function login(email: string, password: string) {
  const res = await fetch(`${config.apiUrl}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  const data = await res.json();
  if (!res.ok || !data.access_token) {
    throw new Error(data.message || "Ошибка авторизации");
  }
  
  // Проверяем, что пользователь является админом (roleId = 1)
  if (data.roleId !== 1) {
    throw new Error("Доступ запрещен. Требуются права администратора.");
  }
  
  return data;
}

export async function getCurrentUser() {
  const token = localStorage.getItem("jwt");
  if (!token) {
    throw new Error("Токен не найден");
  }
  
  const res = await fetch(`${config.apiUrl}/auth/me`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  
  if (!res.ok) {
    throw new Error("Ошибка получения данных пользователя");
  }
  
  const data = await res.json();
  
  // Проверяем, что пользователь является админом (roleId = 1)
  if (data.roleId !== 1) {
    throw new Error("Доступ запрещен. Требуются права администратора.");
  }
  
  return data;
}

export async function getDashboardStats() {
  const token = localStorage.getItem("jwt");
  const res = await fetch(`${config.apiUrl}/admin/dashboard/stats`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.message || "Ошибка загрузки статистики dashboard");
  }
  return await res.json();
}

export async function getCourses() {
  const token = localStorage.getItem("jwt");
  const res = await fetch(`${config.apiUrl}/admin/courses`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Ошибка загрузки курсов");
  const data = await res.json();
  return data.data;
}

export async function searchCourses(search?: string, limit: number = 10, offset: number = 0) {
  const token = localStorage.getItem("jwt");
  const params = new URLSearchParams();
  if (search) params.append('search', search);
  params.append('limit', limit.toString());
  params.append('offset', offset.toString());
  
  const res = await fetch(`${config.apiUrl}/admin/courses/search?${params}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Ошибка поиска курсов");
  const data = await res.json();
  return data;
}

export async function getCourse(id: number) {
  const token = localStorage.getItem("jwt");
  const res = await fetch(`${config.apiUrl}/admin/courses/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Ошибка загрузки курса");
  const data = await res.json();
  return data;
}

export async function updateCourse(id: number, courseData: {
  title: string;
  description?: string;
  shortDescription?: string;
  image_url?: string;
  status?: 'draft' | 'published';
  aiAssistant?: 'gpt' | 'claude';
  dailyAiMessageLimit?: number | string;
  dailyTestLimit?: number | string;
  isPaid?: boolean;
  trialLessonsCount?: number;
}) {
  const token = localStorage.getItem("jwt");
  const res = await fetch(`${config.apiUrl}/admin/courses/${id}`, {
    method: "PUT",
    headers: { 
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}` 
    },
    body: JSON.stringify(courseData),
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.message || "Ошибка обновления курса");
  }
  return await res.json();
}

export async function createCourse(courseData: {
  title: string;
  description?: string;
  shortDescription?: string;
  image_url?: string;
  status?: 'draft' | 'published';
  aiAssistant?: 'gpt' | 'claude';
  dailyAiMessageLimit?: number | string;
  dailyTestLimit?: number | string;
  isPaid?: boolean;
  trialLessonsCount?: number;
}) {
  const token = localStorage.getItem("jwt");
  const res = await fetch(`${config.apiUrl}/admin/courses`, {
    method: "POST",
    headers: { 
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}` 
    },
    body: JSON.stringify(courseData),
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.message || "Ошибка создания курса");
  }
  return await res.json();
}

// Lessons API
export async function getLessons() {
  const token = localStorage.getItem("jwt");
  const res = await fetch(`${config.apiUrl}/admin/lessons`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Ошибка загрузки уроков");
  const data = await res.json();
  return data;
}

export async function getLesson(id: number) {
  const token = localStorage.getItem("jwt");
  const res = await fetch(`${config.apiUrl}/admin/lessons/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Ошибка загрузки урока");
  const data = await res.json();
  return data;
}

export async function createLesson(lessonData: {
  courseId: number;
  title: string;
  content?: string;
  sort_order: number;
  examType: 'test' | 'open';
}) {
  const token = localStorage.getItem("jwt");
  const res = await fetch(`${config.apiUrl}/admin/lessons`, {
    method: "POST",
    headers: { 
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}` 
    },
    body: JSON.stringify(lessonData),
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.message || "Ошибка создания урока");
  }
  return await res.json();
}

export async function updateLesson(id: number, lessonData: {
  courseId?: number;
  title?: string;
  content?: string;
  sort_order?: number;
  examType?: 'test' | 'open';
}) {
  const token = localStorage.getItem("jwt");
  const res = await fetch(`${config.apiUrl}/admin/lessons/${id}`, {
    method: "PUT",
    headers: { 
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}` 
    },
    body: JSON.stringify(lessonData),
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.message || "Ошибка обновления урока");
  }
  return await res.json();
}

// Users API
export async function getUsers() {
  const token = localStorage.getItem("jwt");
  const res = await fetch(`${config.apiUrl}/admin/users`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Ошибка загрузки пользователей");
  const data = await res.json();
  return data;
}

export async function getUser(id: number) {
  const token = localStorage.getItem("jwt");
  const res = await fetch(`${config.apiUrl}/admin/users/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Ошибка загрузки пользователя");
  const data = await res.json();
  return data;
}

export async function updateUser(id: number, userData: {
  email?: string;
  roleId?: number;
}) {
  const token = localStorage.getItem("jwt");
  const res = await fetch(`${config.apiUrl}/admin/users/${id}`, {
    method: "PUT",
    headers: { 
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}` 
    },
    body: JSON.stringify(userData),
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.message || "Ошибка обновления пользователя");
  }
  return await res.json();
}

export async function deleteUser(id: number) {
  const token = localStorage.getItem("jwt");
  const res = await fetch(`${config.apiUrl}/admin/users/${id}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.message || "Ошибка удаления пользователя");
  }
  return await res.json();
}

export async function changeUserPassword(id: number, newPassword: string) {
  const token = localStorage.getItem("jwt");
  const res = await fetch(`${config.apiUrl}/admin/users/${id}/password`, {
    method: "PATCH",
    headers: { 
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}` 
    },
    body: JSON.stringify({ newPassword }),
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.message || "Ошибка смены пароля");
  }
  return await res.json();
}

// API функции для инвайтов
export async function getInvites() {
  const token = localStorage.getItem("jwt");
  const res = await fetch(`${config.apiUrl}/invites/all`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Ошибка загрузки инвайтов");
  return await res.json();
}

export async function createInvite(inviteData: {
  email?: string;
  expiresIn?: string;
}) {
  const token = localStorage.getItem("jwt");
  const res = await fetch(`${config.apiUrl}/invites`, {
    method: "POST",
    headers: { 
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}` 
    },
    body: JSON.stringify(inviteData),
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.message || "Ошибка создания инвайта");
  }
  return await res.json();
}

export async function checkInvite(code: string) {
  const res = await fetch(`${config.apiUrl}/invites/check`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code }),
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.message || "Ошибка проверки инвайта");
  }
  return await res.json();
}

// User Progress API
export async function getUserProgress(userId: number) {
  const token = localStorage.getItem("jwt");
  const res = await fetch(`${config.apiUrl}/admin/users/${userId}/progress`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.message || "Ошибка загрузки прогресса пользователя");
  }
  return await res.json();
}

export async function getUserCourseProgress(userId: number, courseId: number) {
  const token = localStorage.getItem("jwt");
  const res = await fetch(`${config.apiUrl}/admin/users/${userId}/course/${courseId}/progress`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.message || "Ошибка загрузки прогресса по курсу");
  }
  return await res.json();
}

// AI Messages API
export async function getMessages() {
  return getAllMessages();
}

export async function getAllMessages() {
  const token = localStorage.getItem("jwt");
  const res = await fetch(`${config.apiUrl}/admin/messages`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.message || "Ошибка загрузки сообщений");
  }
  return await res.json();
}

export async function getMessage(id: number) {
  const token = localStorage.getItem("jwt");
  const res = await fetch(`${config.apiUrl}/admin/messages/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.message || "Ошибка загрузки сообщения");
  }
  return await res.json();
}

export async function getAllSessions() {
  const token = localStorage.getItem("jwt");
  const res = await fetch(`${config.apiUrl}/admin/sessions`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.message || "Ошибка загрузки сессий");
  }
  return await res.json();
}

export async function getSession(id: number) {
  const token = localStorage.getItem("jwt");
  const res = await fetch(`${config.apiUrl}/admin/sessions/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.message || "Ошибка загрузки сессии");
  }
  return await res.json();
}

// Лимиты API
export async function getUserAiUsage(userId: number) {
  const token = localStorage.getItem("jwt");
  const res = await fetch(`${config.apiUrl}/admin/users/${userId}/ai-usage`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.message || "Ошибка загрузки статистики AI");
  }
  return await res.json();
}

export async function getUserTestUsage(userId: number) {
  const token = localStorage.getItem("jwt");
  const res = await fetch(`${config.apiUrl}/admin/users/${userId}/test-usage`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.message || "Ошибка загрузки статистики тестов");
  }
  return await res.json();
}

export async function resetUserAiUsage(userId: number, courseId: number) {
  const token = localStorage.getItem("jwt");
  const res = await fetch(`${config.apiUrl}/admin/users/${userId}/ai-usage/${courseId}/reset`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.message || "Ошибка сброса AI лимитов");
  }
  return await res.json();
}

export async function resetUserTestUsage(userId: number, courseId: number) {
  const token = localStorage.getItem("jwt");
  const res = await fetch(`${config.apiUrl}/admin/users/${userId}/test-usage/${courseId}/reset`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.message || "Ошибка сброса лимитов тестов");
  }
  return await res.json();
}

// Roadmap API
export const getRoadmaps = async (): Promise<any[]> => {
  const token = localStorage.getItem("jwt");
  const res = await fetch(`${config.apiUrl}/roadmaps`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.message || "Ошибка загрузки roadmap");
  }
  return await res.json();
};

export const getRoadmap = async (id: number): Promise<any> => {
  const token = localStorage.getItem("jwt");
  const res = await fetch(`${config.apiUrl}/roadmaps/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.message || "Ошибка загрузки roadmap");
  }
  return await res.json();
};

export const createRoadmap = async (data: any): Promise<any> => {
  const token = localStorage.getItem("jwt");
  const res = await fetch(`${config.apiUrl}/roadmaps`, {
    method: "POST",
    headers: { 
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}` 
    },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.message || "Ошибка создания roadmap");
  }
  return await res.json();
};

export const updateRoadmap = async (id: number, data: any): Promise<any> => {
  const token = localStorage.getItem("jwt");
  const res = await fetch(`${config.apiUrl}/roadmaps/${id}`, {
    method: "PUT",
    headers: { 
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}` 
    },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.message || "Ошибка обновления roadmap");
  }
  return await res.json();
};

export const deleteRoadmap = async (id: number): Promise<void> => {
  const token = localStorage.getItem("jwt");
  const res = await fetch(`${config.apiUrl}/roadmaps/${id}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.message || "Ошибка удаления roadmap");
  }
};

export const uploadRoadmapImage = async (roadmapId: number, file: File): Promise<any> => {
  const token = localStorage.getItem("jwt");
  const formData = new FormData();
  formData.append('file', file);

  const res = await fetch(`${config.apiUrl}/roadmaps/${roadmapId}/upload-image`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });

  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.message || "Ошибка загрузки изображения roadmap");
  }
  return await res.json();
};

export const uploadRoadmapImageGeneral = async (file: File): Promise<any> => {
  const token = localStorage.getItem("jwt");
  const formData = new FormData();
  formData.append('file', file);

  const res = await fetch(`${config.apiUrl}/roadmaps/upload`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });

  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.message || "Ошибка загрузки изображения roadmap");
  }
  return await res.json();
};

// Course Pricing API
export async function getCoursePricing() {
  const token = localStorage.getItem("jwt");
  const res = await fetch(`${config.apiUrl}/course-pricing`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Ошибка загрузки цен курсов");
  return await res.json();
}

export async function createCoursePricing(pricingData: {
  courseId: number;
  durationDays: number;
  price: number;
  currency?: string;
}) {
  const token = localStorage.getItem("jwt");
  const res = await fetch(`${config.apiUrl}/course-pricing`, {
    method: "POST",
    headers: { 
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}` 
    },
    body: JSON.stringify(pricingData),
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.message || "Ошибка создания цены курса");
  }
  return await res.json();
}

export async function updateCoursePricing(pricingData: {
  id: number;
  courseId?: number;
  durationDays?: number;
  price?: number;
  currency?: string;
}) {
  const token = localStorage.getItem("jwt");
  const res = await fetch(`${config.apiUrl}/course-pricing/${pricingData.id}`, {
    method: "PATCH",
    headers: { 
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}` 
    },
    body: JSON.stringify(pricingData),
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.message || "Ошибка обновления цены курса");
  }
  return await res.json();
}

export async function deleteCoursePricing(id: number) {
  const token = localStorage.getItem("jwt");
  const res = await fetch(`${config.apiUrl}/course-pricing/${id}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.message || "Ошибка удаления цены курса");
  }
  return await res.json();
}

// Balance API

export async function addUserBonus(userId: number, bonusData: {
  amount: number;
  reason: string;
  metadata?: any;
}) {
  const token = localStorage.getItem("jwt");
  const res = await fetch(`${config.apiUrl}/balance/bonus/${userId}`, {
    method: "POST",
    headers: { 
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}` 
    },
    body: JSON.stringify(bonusData),
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.message || "Ошибка добавления бонуса");
  }
  return await res.json();
}

export async function getUserBalanceHistory(userId: number, page: number = 1, limit: number = 20) {
  const token = localStorage.getItem("jwt");
  const res = await fetch(`${config.apiUrl}/balance/user/${userId}/history?page=${page}&limit=${limit}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Ошибка загрузки истории баланса");
  return await res.json();
}

// Course Purchases API
export async function getCoursePurchases() {
  const token = localStorage.getItem("jwt");
  const res = await fetch(`${config.apiUrl}/admin/transactions`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Ошибка загрузки покупок курсов");
  return await res.json();
}

export async function getTransactionStats() {
  const token = localStorage.getItem("jwt");
  const res = await fetch(`${config.apiUrl}/admin/transactions/stats`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Ошибка загрузки статистики транзакций");
  return await res.json();
}

// Course Auditor API

export async function startCourseAudit(auditData: {
  courseId: number;
  auditType: string;
  aiAssistant?: string;
  depth?: number;
}) {
  const token = localStorage.getItem("jwt");
  const res = await fetch(`${config.apiUrl}/course-auditor/start`, {
    method: "POST",
    headers: { 
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}` 
    },
    body: JSON.stringify(auditData),
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.message || "Ошибка запуска аудита");
  }
  return await res.json();
}

export async function getAuditStatus(auditId: number) {
  const token = localStorage.getItem("jwt");
  const res = await fetch(`${config.apiUrl}/course-auditor/audit/${auditId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Ошибка загрузки статуса аудита");
  return await res.json();
}

export async function getAuditReport(auditId: number) {
  const token = localStorage.getItem("jwt");
  const res = await fetch(`${config.apiUrl}/course-auditor/audit/${auditId}/report`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Ошибка загрузки отчета аудита");
  return await res.json();
}

export async function getCourseAudits(courseId: number) {
  const token = localStorage.getItem("jwt");
  const res = await fetch(`${config.apiUrl}/course-auditor/course/${courseId}/audits`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Ошибка загрузки аудитов курса");
  return await res.json();
}

export async function getAuditDashboard(limit?: number) {
  const token = localStorage.getItem("jwt");
  const url = limit 
    ? `${config.apiUrl}/course-auditor/dashboard?limit=${limit}`
    : `${config.apiUrl}/course-auditor/dashboard`;
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Ошибка загрузки дашборда аудитов");
  return await res.json();
}

export async function getAvailableCourses() {
  const token = localStorage.getItem("jwt");
  const res = await fetch(`${config.apiUrl}/course-auditor/courses/available`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Ошибка загрузки доступных курсов");
  return await res.json();
}